export interface CookieCategory {
  id: string;
  name: string;
  description: string;
  required: boolean;
}

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
  timestamp?: number;
  consentId?: string;
}

export const COOKIE_CATEGORIES: CookieCategory[] = [
  {
    id: 'necessary',
    name: 'Necessary Cookies',
    description: 'These cookies are essential for the website to function properly. They enable basic functions like page navigation and access to secure areas of the website.',
    required: true,
  },
  {
    id: 'functional',
    name: 'Functional Cookies',
    description: 'These cookies enable personalized features and functionality, such as remembering your preferences, language choice, or region.',
    required: false,
  },
  {
    id: 'analytics',
    name: 'Analytics Cookies',
    description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
    required: false,
  },
  {
    id: 'marketing',
    name: 'Marketing Cookies',
    description: 'These cookies are used to track visitors across websites to display ads that are relevant and engaging for individual users.',
    required: false,
  },
];

const CONSENT_COOKIE_NAME = 'gdpr-consent';
const CONSENT_DURATION_DAYS = 365;

export const getStoredConsent = (): CookiePreferences | null => {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(CONSENT_COOKIE_NAME);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Check if consent is still valid (within 365 days)
      if (parsed.timestamp && Date.now() - parsed.timestamp < CONSENT_DURATION_DAYS * 24 * 60 * 60 * 1000) {
        return parsed;
      }
    }
  } catch (error) {
    console.error('Error reading consent preferences:', error);
  }

  return null;
};

export const saveConsent = (preferences: CookiePreferences): void => {
  if (typeof window === 'undefined') return;

  const consentData = {
    ...preferences,
    timestamp: Date.now(),
    consentId: generateConsentId(),
  };

  try {
    localStorage.setItem(CONSENT_COOKIE_NAME, JSON.stringify(consentData));

    // Also set a cookie for server-side detection
    document.cookie = `${CONSENT_COOKIE_NAME}=true; max-age=${CONSENT_DURATION_DAYS * 24 * 60 * 60}; path=/; SameSite=Lax`;

    // Dispatch custom event for other components to react
    window.dispatchEvent(new CustomEvent('cookieConsentUpdate', { detail: consentData }));

    // Apply the preferences
    applyPreferences(consentData);

    // Handle analytics consent changes
    // Dynamic import to avoid circular dependency
    import('./analytics').then(({ handleConsentChange }) => {
      handleConsentChange(consentData);
    });
  } catch (error) {
    console.error('Error saving consent preferences:', error);
  }
};

export const revokeConsent = (): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(CONSENT_COOKIE_NAME);
    document.cookie = `${CONSENT_COOKIE_NAME}=; max-age=0; path=/`;

    // Remove all non-necessary cookies
    deleteNonEssentialCookies();

    // Reload page to ensure scripts are not loaded
    window.location.reload();
  } catch (error) {
    console.error('Error revoking consent:', error);
  }
};

const generateConsentId = (): string => {
  return `consent_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const applyPreferences = (preferences: CookiePreferences): void => {
  // Analytics consent is now handled in analytics.ts
  // This ensures GA doesn't load at all without consent

  if (!preferences.analytics) {
    // Delete any existing Google Analytics cookies
    deleteGoogleAnalyticsCookies();
  }

  if (!preferences.marketing) {
    // Delete any marketing/advertising cookies
    deleteMarketingCookies();
  }

  if (!preferences.functional) {
    // Clear any functional cookies/storage
    clearFunctionalStorage();
  }
};

const deleteGoogleAnalyticsCookies = (): void => {
  const gaCookies = ['_ga', '_gid', '_gat', '_gac_'];
  const domain = window.location.hostname;

  gaCookies.forEach(cookieName => {
    // Delete for current domain
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    // Delete for parent domain
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
    // Delete for .domain.com
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`;
  });
};

const deleteMarketingCookies = (): void => {
  // Common marketing/advertising cookie patterns
  const marketingPatterns = ['_fbp', '_fbc', 'fr', 'tr', '_gcl_', '_gac_', '_dc_gtm_', 'IDE', 'test_cookie', 'ads_'];
  const cookies = document.cookie.split(';');
  const domain = window.location.hostname;

  cookies.forEach(cookie => {
    const name = cookie.split('=')[0].trim();
    if (marketingPatterns.some(pattern => name.includes(pattern))) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`;
    }
  });
};

const clearFunctionalStorage = (): void => {
  // Clear non-essential localStorage items
  const essentialKeys = [CONSENT_COOKIE_NAME];
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && !essentialKeys.includes(key)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(key => localStorage.removeItem(key));

  // Clear sessionStorage except essentials
  sessionStorage.clear();
};

const deleteNonEssentialCookies = (): void => {
  // Get all cookies
  const cookies = document.cookie.split(';');

  // List of essential cookies to keep
  const essentialCookies = [CONSENT_COOKIE_NAME];

  cookies.forEach(cookie => {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

    if (!essentialCookies.includes(name)) {
      // Delete cookie
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
    }
  });
};

// Initialize consent state on page load
export const initializeConsent = (): void => {
  if (typeof window === 'undefined') return;

  // Check for Do Not Track preference
  const dnt = navigator.doNotTrack || (window as any).doNotTrack || (navigator as any).msDoNotTrack;
  const dntEnabled = dnt === '1' || dnt === 'yes';

  if (dntEnabled) {
    // Auto-reject all non-essential cookies if DNT is enabled
    console.log('Do Not Track detected - rejecting non-essential cookies');
    const dntPreferences: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    };

    // Don't show popup if DNT is enabled, just apply minimal settings
    applyPreferences(dntPreferences);
    return;
  }

  // Apply stored preferences if they exist
  const storedConsent = getStoredConsent();
  if (storedConsent) {
    applyPreferences(storedConsent);
  } else {
    // No consent yet - ensure all non-essential cookies are blocked
    deleteNonEssentialCookies();
  }
};

// Check if user has given any consent
export const hasUserConsented = (): boolean => {
  return getStoredConsent() !== null;
};

// Get current consent status for a specific category
export const getCategoryConsent = (category: keyof CookiePreferences): boolean => {
  const consent = getStoredConsent();
  return consent ? consent[category] as boolean : category === 'necessary';
};